import type { MerchantPermissionId } from "@/modules/roles/domain/contracts/merchant-permissions";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import type { ShopVerifiedLevel } from "../api/client";

const LEVEL_ORDER: Record<ShopVerifiedLevel, number> = {
  none: 0,
  basic: 1,
  verified: 2,
};

/** ระดับต่ำสุดต่อ permission — สอดคล้อง backend contract */
const MIN_LEVEL_BY_PERMISSION: Partial<
  Record<MerchantPermissionId, ShopVerifiedLevel>
> = {
  [MerchantPermissions.catalog.read]: "basic",
  [MerchantPermissions.catalog.create]: "basic",
  [MerchantPermissions.catalog.update]: "basic",
  [MerchantPermissions.catalog.delete]: "basic",
  [MerchantPermissions.inventory.read]: "basic",
  [MerchantPermissions.inventory.adjust]: "basic",
  [MerchantPermissions.inventory.receive]: "basic",
  [MerchantPermissions.inventory.transfer]: "basic",
  [MerchantPermissions.pos.read]: "basic",
  [MerchantPermissions.pos.sell]: "basic",
  [MerchantPermissions.pos.refund]: "basic",
  [MerchantPermissions.pos.void]: "basic",
  [MerchantPermissions.settlement.read]: "basic",
  [MerchantPermissions.promotions.read]: "basic",
  [MerchantPermissions.promotions.manage]: "basic",
  [MerchantPermissions.settlement.requestPayout]: "verified",
  [MerchantPermissions.shop_staff.read]: "none",
  [MerchantPermissions.shop_staff.create]: "basic",
  [MerchantPermissions.shop_staff.update]: "basic",
  [MerchantPermissions.shop_staff.delete]: "basic",
  [MerchantPermissions.shop_roles.read]: "none",
  [MerchantPermissions.shop_roles.create]: "basic",
  [MerchantPermissions.shop_roles.update]: "basic",
  [MerchantPermissions.shop_roles.delete]: "basic",
  [MerchantPermissions.shifts.read]: "none",
  [MerchantPermissions.shifts.manage]: "basic",
  [MerchantPermissions.shifts.clock]: "basic",
  [MerchantPermissions.claims.read]: "basic",
  [MerchantPermissions.claims.create]: "basic",
  [MerchantPermissions.claims.manage]: "basic",
  [MerchantPermissions.workshop.read]: "basic",
  [MerchantPermissions.workshop.create]: "basic",
  [MerchantPermissions.workshop.sell]: "basic",
  [MerchantPermissions.workshop.manage]: "basic",
};

const DEFAULT_MIN: ShopVerifiedLevel = "basic";

/**
 * ตรวจว่า verifiedLevel ปัจจุบันเพียงพอต่อการใช้ permission นี้หรือไม่
 * ใช้ซ่อน/disable เมนูหรือปุ่มตามระดับการยืนยันร้าน
 */
export function canUsePermissionWithLevel(
  permissionId: MerchantPermissionId,
  verifiedLevel: ShopVerifiedLevel | null | undefined,
): boolean {
  const level = verifiedLevel ?? "none";
  const minRequired = MIN_LEVEL_BY_PERMISSION[permissionId] ?? DEFAULT_MIN;
  return LEVEL_ORDER[level] >= LEVEL_ORDER[minRequired];
}

/** คืนระดับต่ำสุดที่ต้องมีเพื่อใช้ permission นี้ */
export function getMinVerifiedLevelForPermission(
  permissionId: MerchantPermissionId,
): ShopVerifiedLevel {
  return MIN_LEVEL_BY_PERMISSION[permissionId] ?? DEFAULT_MIN;
}

/** label สั้นสำหรับแสดงใน badge */
export const VERIFIED_LEVEL_LABELS: Record<ShopVerifiedLevel, string> = {
  none: "ຍັງບໍ່ຢືນຢັນ",
  basic: "ຢືນຢັນເບື້ອງຕົ້ນ",
  verified: "ຢືນຢັນຄົບ",
};
